/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect } from 'react';
import { ExpandablePanel } from '@entur/expand';
import { useIntl } from 'react-intl';
import ErrorMessage from '../ErrorMessage/index';

interface ExpandableHandlerProps {
  title?: React.ReactNode;
  render?: React.ReactNode;
  isLinked?: boolean;
  defaultOpen?: boolean;
  removeIsLinked?: () => void;
  scroll?: () => void;
  empty?: boolean;
  expanded?: boolean;
  click?: boolean;
  isFiltered?: boolean;
  hasExpanded?: (open: boolean) => void;
  hasClosed?: (open: boolean) => void;
  onlyClose?: boolean;
  shouldScroll?: boolean;
  /** Render `render` only once the panel has been opened, so content that fetches does so on demand. */
  lazy?: boolean;
}

const ExpandableHandler = ({
  title,
  render,
  isLinked = false,
  defaultOpen = false,
  removeIsLinked = () => {},
  scroll,
  empty = false,
  expanded = false,
  click = false,
  isFiltered = false,
  hasExpanded,
  hasClosed,
  onlyClose = false,
  shouldScroll = false,
  lazy = false,
}: ExpandableHandlerProps) => {
  const intl = useIntl();
  const [open, isOpen] = useState(defaultOpen);
  const [onStartUp, setOnStartUp] = useState(true);
  const [hasOpened, setHasOpened] = useState(false);
  if ((open || isLinked) && !hasOpened) setHasOpened(true);
  const noData = () => (
    <ErrorMessage variant="info" value={intl.formatMessage({ id: 'settlementImport.noSalesAnnexes' })} small />
  );

  useEffect(() => {
    if (hasExpanded && open) {
      hasExpanded(open);
    }
    if (hasClosed && !open) {
      hasClosed(open);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (isLinked || click) {
      isOpen(true);
    }
  }, [click, isLinked]);

  useEffect(() => {
    if ((!onStartUp || expanded) && !onlyClose) {
      isOpen(expanded);
    } else if (!onStartUp && onlyClose) {
      if (!expanded) {
        isOpen(expanded);
      }
    }
  }, [expanded]); //eslint-disable-line

  useEffect(() => {
    if (onStartUp) {
      setOnStartUp(false);
    }
  }, [onStartUp]);

  useEffect(() => {
    if (defaultOpen) isOpen(true);
  }, [defaultOpen]);

  const view = (
    <ExpandablePanel
      title={title}
      open={isLinked || open}
      style={isFiltered ? { backgroundColor: 'var(--cleos-frame-selected)' } : undefined}
      onToggle={() => {
        if (isLinked) {
          removeIsLinked();
        }
        isOpen(!open);
      }}
    >
      {empty ? noData() : lazy && !hasOpened ? null : render}
    </ExpandablePanel>
  );

  useEffect(() => {
    if (isLinked && scroll && shouldScroll) {
      scroll();
    }
  }, [isLinked, scroll, shouldScroll]);

  return view;
};

export default ExpandableHandler;
